import { Role } from "@prisma/client";
import { type Session } from "@/types/auth";

export const userIsAdmin = (session: Session | null) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    return session.customers.some(
        (c) =>
            c.role === Role.ADMIN ||
            c.role === Role.DEV_ADMIN ||
            c.role === Role.HEAD_ADMIN ||
            c.role === Role.UNIVERSAL_ADMIN ||
            c.role === Role.UNIVERSAL_DEV_ADMIN ||
            c.role === Role.UNIVERSAL_HEAD_ADMIN ||
            c.role === Role.SUPER_ADMIN
    );
}

export const userIsSuperAdmin = (session: Session | null) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    return session.customers.some((c) => c.role === Role.SUPER_ADMIN);
}

export const userIsUniversal = (session: Session | null) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    return session.customers.some(
        (c) =>
            c.role === Role.UNIVERSAL_USER ||
            c.role === Role.UNIVERSAL_ADMIN ||
            c.role === Role.UNIVERSAL_DEV_ADMIN ||
            c.role === Role.UNIVERSAL_HEAD_ADMIN ||
            c.role === Role.SUPER_ADMIN
    );
}

export const userIsUniversalAdmin = (session: Session | null) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    return session.customers.some(
        (c) =>
            c.role === Role.UNIVERSAL_ADMIN ||
            c.role === Role.UNIVERSAL_DEV_ADMIN ||
            c.role === Role.UNIVERSAL_HEAD_ADMIN ||
            c.role === Role.SUPER_ADMIN
    );
}

export const userIsUniversalDevAdmin = (session: Session | null) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    return session.customers.some(
        (c) =>
            c.role === Role.UNIVERSAL_DEV_ADMIN ||
            c.role === Role.UNIVERSAL_HEAD_ADMIN ||
            c.role === Role.SUPER_ADMIN
    );
}

export const userIsUniversalHeadAdmin = (session: Session | null) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    return session.customers.some(
        (c) =>
            c.role === Role.UNIVERSAL_HEAD_ADMIN ||
            c.role === Role.SUPER_ADMIN
    );
}

export const userIsAuthorizedForCustomer = (session: Session | null, customer: string) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    if (userIsUniversal(session)) return true;
    const customerIDs = session.customers.map((c) => c.customer_cuid);
    return customerIDs.includes(customer);
}

export const userIsAuthorizedForCustomers = (session: Session | null, customer: string[]) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    if (userIsUniversal(session)) return true;
    const customerIDs = session.customers.map((c) => c.customer_cuid);
    return customer.every((c) => customerIDs.includes(c));
}

export const userIsAdminForCustomer = (session: Session | null, customer: string) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    if (userIsUniversalAdmin(session)) return true;
    const foundCustomer = session.customers.find((c) => c.customer_cuid === customer);
    return foundCustomer && (
        foundCustomer.role === Role.ADMIN ||
        foundCustomer.role === Role.DEV_ADMIN ||
        foundCustomer.role === Role.HEAD_ADMIN
    );
}

export const userIsHeadAdminForCustomer = (session: Session | null, customer: string) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    if (userIsUniversalAdmin(session)) return true;
    const foundCustomer = session.customers.find((c) => c.customer_cuid === customer);
    return foundCustomer && foundCustomer.role === Role.HEAD_ADMIN
}

export const userCanManageRoleForCustomer = (session: Session | null, customer: string, role: Role) => {
    if (!userIsAdminForCustomer(session, customer)) return false;

    if (role === Role.USER) return true;
    if (role === Role.ADMIN || role === Role.DEV_ADMIN) {
        return !!userIsHeadAdminForCustomer(session, customer);
    }
    if (role === Role.HEAD_ADMIN || role === Role.UNIVERSAL_USER) {
        return !!userIsUniversalAdmin(session);
    }
    if (role === Role.UNIVERSAL_ADMIN || role === Role.UNIVERSAL_DEV_ADMIN) {
        return !!userIsUniversalHeadAdmin(session);
    }
    return userIsSuperAdmin(session);
}

export const getUserIdFromSessionForCustomer = (session: Session | null, customer: string) => {
    if (!session?.customers || session.customers?.length === 0) return false;
    if (userIsUniversal(session)) {
        return session.user?.id;
    }
    const foundCustomer = session.customers.find((c) => c.customer_cuid === customer);
    return foundCustomer?.user_id;
}

export const userIsDevAdminForCustomer = (session: Session | null, customer: string) => {
    if (!session || session.customers.length === 0) return false;
    if (userIsUniversalAdmin(session)) return true;
    const foundCustomer = session.customers.find((c) => c.customer_cuid === customer);
    return foundCustomer && (
        foundCustomer.role === Role.DEV_ADMIN ||
        foundCustomer.role === Role.HEAD_ADMIN
    );
}

export const userHasAdminLevelForCustomers = (session: Session | null, customers: Array<string>, adminLevel: Array<Role>) => {
    if (!session || session.customers.length === 0) return false;
    if (userIsUniversalAdmin(session)) return true;
    // Check that each customer in the array is in the user's customers and has adminLevel role for the customer
    return customers.every((customer) => {
        const foundCustomer = session.customers.find((c) => c.customer_cuid === customer);
        return foundCustomer && adminLevel.includes(foundCustomer.role);
    });
}

// export const getUserRoleForCustomer = (session: Session | null, customer: string) => {
//     if (!session || session.customers.length === 0) return;
//     const foundCustomer = session.customers.find((c) => c.customer_cuid === customer);
//     return foundCustomer?.role;
// }

export const getAdminCustomerFromSession = (session: Session | null) => {
    if (!session || session.customers.length === 0) return;
    return session.customers.find(
        (c) =>
            c.role === Role.ADMIN ||
            c.role === Role.DEV_ADMIN ||
            c.role === Role.HEAD_ADMIN
    );
}

// export const showUniversalAdminControls = (session: Session) => {
//     if (!session) return false;
//     if (session.customers.length === 0) return false;
//     return session.customers.some(
//         (c) =>
//             c.role === Role.UNIVERSAL_ADMIN ||
//             c.role === Role.UNIVERSAL_DEV_ADMIN ||
//             c.role === Role.UNIVERSAL_SUPER_ADMIN
//     );
// }
